import React from "react";
import { Formik, FormikErrors, FormikHelpers } from 'formik';
import { useModals } from "@mantine/modals";
import { Alert, Box, Button, Grid, Group, ScrollArea, Stepper, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";

import { buildFormConfig, FormConfig } from "src/utils/forms";

import { GenericError } from "src/state/_types";

import { Maybe } from "src/state/gql/_types";
import { changeEmptyPropsDeep } from "src/utils/objects";
import { FieldRenderer } from "src/components/forms/FieldRenderer"
import { Icon } from "@iconify/react/dist/iconify.js";
import { Icons } from "../ui/Icons";
import { notifications } from '@mantine/notifications';

type GenericDictionary = {
    [index: string]: any;
}

type GenericModel = {
    id: string;
    [index: string]: any;
}

type FormDrawerProps<T extends GenericDictionary, M extends GenericModel, I extends any> = {
    actionTitle?: string;
    formSchema: any;
    createAction?(value: any): Promise< Maybe<Pick<M, 'id'>> | Maybe<{returning: Array<M>}> | undefined>;
    bulkUpdateAction?(value: any): Promise<Maybe<Maybe<{ returning: M[] }>[]> | undefined>
    updateAction?(value: any): Promise<Maybe<M> | undefined>;
    deleteAction?(value: any): Promise<Maybe<Pick<M, 'id'>> | undefined>;
    resetAction?(): void;
    onClose?(item?: Pick<M, 'id'>): void;
    onClose?(items?: Maybe<Maybe<{ returning: M[]; }>[]> | undefined): void;
    isLoading: boolean;
    errors: GenericError[];
    currentItemId: I | null;
    initialData: T;
    currentItemData: T | null
    autoOpen?: boolean;
    hideDelete?: boolean;
    actionColor?: string;
    requiredDirty?: boolean;
    redirectPath?: string;
    createRedirect?: boolean;
    updateRedirect?: boolean;
    deleteRedirect?: boolean;
    validators?: GenericDictionary;
    renderReviewComponent?(data: T): React.ReactElement | null;
    validateOnMount?: boolean;
}

export type FormComponentProps<T extends GenericDictionary> = {
    actionTitle?: string | undefined;
    currentItem: T | null;
    onFormReset?(): void;
    onFormAction?(): void;
    onClose?(): void;
    preselectKey?: string;
    preselectValue?: any;
    actionColor?: string;
}


export function FormDrawer<PayloadType extends GenericDictionary, ModelType extends GenericModel, IDType extends string>({
    formSchema,
    createAction,
    updateAction,
    deleteAction,
    resetAction,
    bulkUpdateAction,
    onClose,
    errors,
    currentItemId,
    currentItemData,
    initialData,
    hideDelete = true,
    requiredDirty = true,
    redirectPath,
    deleteRedirect = false,
    createRedirect = false,
    updateRedirect = false,
    validators = {},
    renderReviewComponent = undefined,
    validateOnMount = false
}: React.PropsWithChildren<FormDrawerProps<PayloadType, ModelType, IDType>>): React.ReactElement | null {

    const modals = useModals();
    const navigate = useNavigate();
    const [formMode, setFormMode] = React.useState<'create' | 'update' | 'bulkUpdate' | null>(null);
    const [initialState] = React.useState<PayloadType>(currentItemData || initialData);
    const [formConfig, setFormConfig] = React.useState<FormConfig<ModelType> | null>(null)
    const [activeGroup, setActiveGroup] = React.useState(0);
    const [subsAreValid, setSubsAreValid] = React.useState<boolean>(true);

    if (!deleteAction) hideDelete = true

    React.useEffect(() => {
        if (currentItemData && formMode !== 'update') {

            setFormMode('update');
            let config = buildFormConfig<ModelType>(formSchema, validators, 'update')
            setFormConfig(config)

        } else if (!!bulkUpdateAction && formMode !== 'bulkUpdate') {

            setFormMode('bulkUpdate');
            let config = buildFormConfig<ModelType>(formSchema, validators, 'create')
            setFormConfig(config)

        }else if (!currentItemData && formMode !== 'create' && formMode !== 'bulkUpdate' && formMode !== 'update') {

            setFormMode('create');
            let config = buildFormConfig<ModelType>(formSchema, validators, 'create')
            setFormConfig(config)

        }
    }, [formMode, formSchema, validators, currentItemData, initialData]);

    const checkSubsAreValid = (valid: boolean) => {
        setSubsAreValid(valid)
    }
    const openDeleteModal = () =>
        modals.openConfirmModal({
            title: 'Delete Item?',
            zIndex: 999,
            centered: true,
            children: (
                <Text size="sm">
                    Are you sure you want to delete this item? This action is destructive and cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: "No, Dont Delete" },
            confirmProps: { color: 'red' },
            onConfirm: () => handleDelete(),
        });

    const handleClose = async () => {
        if (currentItemData && resetAction) {
            resetAction();
        } else {
            if (onClose) {
                onClose()
            }
        }
    }

    const handleDelete = async () => {
        if (formMode === 'update' && currentItemId && deleteAction) {
            let deletedItem = await deleteAction({ id: currentItemId });
            if (deletedItem) {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'green',
                    autoClose: 4000,
                    title: 'Success!',
                    message: `${formConfig?.title || 'Item'} Deleted`
                })
                if (resetAction) resetAction();
                if (onClose) {
                    onClose(deletedItem)
                    if (deleteRedirect && redirectPath) {
                        navigate(redirectPath)
                    }
                }
            } else {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'red',
                    autoClose: 4000,
                    title: 'Opps!',
                    message: `Could Not Delete ${formConfig?.title || 'Item'}`
                })
            }
        }
    }

    const getEditablePayload = (payload: PayloadType): PayloadType => {
        let editablePayload: GenericDictionary = {};
        Object.keys(payload).forEach((payloadKey) => {
            if (formConfig && formConfig.editable.indexOf(payloadKey) > -1) {
                editablePayload[payloadKey] = payload[payloadKey]
            }
        });
        return editablePayload as PayloadType;
    }

    const onSubmit = async (payload: PayloadType, { resetForm }: FormikHelpers<PayloadType>) => {
        let finalPayload = changeEmptyPropsDeep(payload)
        if (formMode === 'create' && createAction) {
            let newItem = await createAction(finalPayload);
            if (newItem) {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'green',
                    autoClose: 4000,
                    title: 'Success!',
                    message: `${formConfig?.title || 'Item'} Created`
                })
                resetForm();
                if (onClose) {
                    onClose()
                    if (createRedirect) {
                        //navigate(`${redirectPath}/${newItem.id}`)
                    }
                }
            } else {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'red',
                    autoClose: 4000,
                    title: 'Opps!',
                    message: `Could Not Create ${formConfig?.title || 'Item'}`
                })
            }
        }
        if (formMode === 'update' && currentItemId && updateAction) {
            let editablePayload = getEditablePayload(finalPayload as PayloadType);
            let updatedItem = await updateAction({ id: currentItemId, _set: editablePayload });
            if (updatedItem) {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'green',
                    autoClose: 4000,
                    title: 'Success!',
                    message: `${formConfig?.title || 'Item'} Updated`
                })
                if (resetAction) resetAction();
                resetForm();
                if (onClose) {
                    onClose(updatedItem)
                    if (updateRedirect && redirectPath) {
                        navigate(redirectPath)
                    }
                }
            } else {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'red',
                    autoClose: 4000,
                    title: 'Opps!',
                    message: `Could Not Update ${formConfig?.title || 'Item'}`
                })
            }
        }
        if (formMode === 'bulkUpdate' && bulkUpdateAction) {
            let editablePayload = getEditablePayload(finalPayload as PayloadType);
            let updatedItems = await bulkUpdateAction(editablePayload);
            if (updatedItems) {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'green',
                    autoClose: 4000,
                    title: 'Success!',
                    message: `${formConfig?.title || 'Item'} Updated`
                })
                if (resetAction) resetAction();
                resetForm();
                if (onClose) {
                    onClose(updatedItems)
                    if (updateRedirect && redirectPath) {
                        navigate(redirectPath)
                    }
                }
            } else {
                notifications.show({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'red',
                    autoClose: 4000,
                    title: 'Opps!',
                    message: `Could Not Update ${formConfig?.title || 'Item'}`
                })
            }
        }
    }

    const displayErrors = () => {
        if (!errors.length) return null;
        return (
            <Alert title="Error" variant="outline" m={7} color="red">
                {errors.map((error, key) => {
                    return (
                        <span key={`error-${key}`}>{error.text}</span>
                    )
                })}
            </Alert>
        )
    }

    function onKeyDown(keyEvent: any) {
        if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
            keyEvent.preventDefault();
        }
    }

    if (!formMode || !formConfig) return null;

    const groupConfigs: {
        [index: string]: any
    } = {}

    if (formConfig.groups && Object.keys(formConfig.groups).length) {

        Object.keys(formConfig.groups).forEach(i => {

            if (formConfig.groups) {

                let group = formConfig.groups[i]

                if (!groupConfigs.hasOwnProperty(i)) {
                    groupConfigs[i] = {
                        id: i,
                        title: group.label,
                        fields: {},
                        editable: formConfig.editable
                    }
                }

            }

        })

    }

    Object.keys(formConfig.fields).forEach(i => {

        let field = formConfig.fields[i]

        if (!!field.group) {

            if (groupConfigs.hasOwnProperty(field.group)) {

                groupConfigs[field.group].fields[i] = field

            } else {

                if (!groupConfigs.hasOwnProperty('default')) {
                    groupConfigs.default = {
                        id: 'default',
                        title: 'Overview',
                        fields: {},
                        editable: formConfig.editable
                    }
                }
                groupConfigs.default.fields[i] = field

            }

        }

    })

    let stepsCount = !!formConfig.groups ? Object.keys(formConfig.groups).length : 0
    let isLastStep = activeGroup === stepsCount - (renderReviewComponent ? 0 : 1)
    let isFirstStep = activeGroup === 0
    let currentGroup = Object.values(groupConfigs)[activeGroup]

    const nextStep = () => setActiveGroup((current) => (current < stepsCount ? current + 1 : current));
    const prevStep = () => setActiveGroup((current) => (current > 0 ? current - 1 : current));

    const getCurrentGroupErrors = (errors: FormikErrors<PayloadType>): FormikErrors<PayloadType> | false => {

        var groupErrors: FormikErrors<PayloadType> = {}

        Object.keys(errors).forEach(k => {

            var error = errors[k]

            if (currentGroup.fields.hasOwnProperty(k)) {
                groupErrors[k as keyof PayloadType] = error
            }

        })

        return Object.keys(groupErrors).length ? groupErrors : false

    }

    const renderGrouped = () => {
        return (
            <Formik
                onKeyDown={onKeyDown}
                initialValues={initialState}
                onSubmit={onSubmit}
                validationSchema={formConfig.validationSchema}
                validateOnBlur={true}
                validateOnMount={validateOnMount}
                validateOnChange={true}
            >
                {({ values, errors, handleChange, handleBlur, handleSubmit, submitForm, isSubmitting, isValid, dirty, setFieldValue }) => {

                    let groupErrors = getCurrentGroupErrors(errors)
                    let isCurrentGroupValid = !Object.keys(groupErrors).length

                    return (
                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            style={{
                                height: "100%",
                                display: "grid",
                                gridTemplateRows: "1fr auto"
                            }}
                        >

                            <Box style={{ position: 'relative', height: '100%' }}>

                                <Stepper
                                    active={activeGroup}
                                    onStepClick={setActiveGroup}
                                    radius="sm"
                                    styles={{
                                        root: {
                                            position: 'relative',
                                            height: '100%'
                                        },
                                        steps: {
                                            padding: 'var(--mantine-spacing-xl)',
                                            paddingBottom: 0,
                                            borderRadius: 'var(--mantine-radius-sm)',
                                            backgroundColor: 'var(--mantine-color-white)'
                                        },
                                        content: {
                                            position: 'relative',
                                            height: `calc(100% - 75px)`
                                        }
                                    }}
                                >

                                    {Object.keys(groupConfigs).map((i, k) => {

                                        let groupConfig = groupConfigs[i] as FormConfig<ModelType>
                                        if (!Object.keys(groupConfig.fields).length) return null

                                        const renderFields = () => (
                                            <ScrollArea
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0
                                                }}
                                            >

                                                <Box p="xl">

                                                    <Grid gutter="xs" style={{ margin: 0 }}>

                                                        <FieldRenderer
                                                            currentItemId={currentItemId}
                                                            formConfig={groupConfig}
                                                            formMode={formMode}
                                                            values={values}
                                                            errors={errors}
                                                            setFieldValue={setFieldValue}
                                                            handleBlur={handleBlur}
                                                            handleChange={handleChange}
                                                            checkSubsAreValid={checkSubsAreValid}
                                                        />

                                                    </Grid>

                                                    <Box>
                                                        {displayErrors()}
                                                    </Box>

                                                </Box>

                                            </ScrollArea>
                                        )

                                        return (
                                            <Stepper.Step
                                                key={k}
                                                label={`Step ${k + 1}`}
                                                description={groupConfig.title}
                                                allowStepSelect={false}
                                            >

                                                {renderFields()}

                                            </Stepper.Step>
                                        )
                                    })}

                                    {renderReviewComponent&&(
                                        <Stepper.Step
                                            label={`Step ${stepsCount + 1}`}
                                            description="Review"
                                            allowStepSelect={false}
                                        >
                                            {renderReviewComponent(values)}
                                        </Stepper.Step>
                                    )}

                                </Stepper >

                                <Box>
                                    {displayErrors()}
                                </Box>

                            </Box>

                            <Box
                                className="border-color-theme-global"
                                style={(theme) => ({
                                    padding: theme.spacing.xl,
                                    borderTopStyle: 'solid',
                                    borderTopWidth: '1px'
                                })}
                            >

                                <Group justify="space-between">

                                    <Box>
                                        {formMode === 'update' && !hideDelete ? (
                                            <Button
                                                color="red"
                                                onClick={openDeleteModal}
                                            >
                                                Delete
                                            </Button>
                                        ) : null}
                                    </Box>

                                    <Group >

                                        {isFirstStep && (
                                            <Button
                                                variant="default"
                                                onClick={handleClose}>
                                                Cancel
                                            </Button>
                                        )}

                                        {stepsCount > 1 && activeGroup > 0 && (
                                            <Button
                                                variant="default"
                                                onClick={prevStep}
                                                loading={isSubmitting}
                                            >
                                                Previous Step
                                            </Button>
                                        )}

                                        {stepsCount > 1 && !isLastStep && (
                                            <Button
                                                color="blue"
                                                onClick={nextStep}
                                                loading={isSubmitting}
                                                disabled={requiredDirty ? !dirty || !isCurrentGroupValid || !subsAreValid : false}
                                            >
                                                Next Step
                                            </Button>
                                        )}

                                        {isLastStep && (
                                            <Button
                                                color="green"
                                                onClick={submitForm}
                                                loading={isSubmitting}
                                                disabled={requiredDirty ? !dirty || !isValid || !subsAreValid : false}
                                            >
                                                Save
                                            </Button>
                                        )}
                                    </Group>

                                </Group>
                            </Box>

                        </Box>
                    )

                }}

            </Formik>
        )
    }

    const renderDefault = () => {
        return (
            <Formik
                onKeyDown={onKeyDown}
                initialValues={initialState}
                onSubmit={onSubmit}
                validationSchema={formConfig.validationSchema}
                validateOnBlur={true}
                validateOnMount={validateOnMount}
                validateOnChange={true}
            >
                {({ values, errors, handleChange, handleBlur, handleSubmit, submitForm, isSubmitting, isValid, dirty, setFieldValue }) => {

                    return (

                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            style={{
                                height: "100%",
                                display: "grid",
                                gridTemplateRows: "1fr auto",
                            }}
                        >

                            <Box style={{ position: 'relative', height: '100%' }}>
                                <ScrollArea
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        bottom: 0,
                                        left: 0,
                                        right: 0
                                    }}
                                >

                                    <Box p="xl">

                                        <Grid gutter="xs" style={{ margin: 0 }}>

                                            <FieldRenderer
                                                currentItemId={currentItemId}
                                                formConfig={formConfig}
                                                formMode={formMode}
                                                values={values}
                                                errors={errors}
                                                setFieldValue={setFieldValue}
                                                handleBlur={handleBlur}
                                                handleChange={handleChange}
                                            />

                                        </Grid>

                                        <Box>
                                            {displayErrors()}
                                        </Box>

                                    </Box>

                                </ScrollArea>
                            </Box>

                            <Box
                                className="border-color-theme-global"
                                style={(theme) => ({
                                    padding: theme.spacing.xl,
                                    borderTopStyle: 'solid',
                                    borderTopWidth: '1px'
                                })}
                            >
                                <Group justify="space-between">
                                    <Box>
                                        {formMode === 'update' && !hideDelete ? (
                                            <Button
                                                color="red"
                                                onClick={openDeleteModal}
                                            >
                                                Delete
                                            </Button>
                                        ) : null}
                                    </Box>
                                    <Group >
                                        <Button
                                            variant="default"
                                            onClick={handleClose}>
                                            Cancel
                                        </Button>
                                        <Button
                                            color="green"
                                            onClick={submitForm}
                                            loading={isSubmitting}
                                            disabled={requiredDirty ? !dirty || !isValid : false}
                                        >
                                            Save
                                        </Button>
                                    </Group>
                                </Group>
                            </Box>

                        </Box>

                    )
                }}

            </Formik>
        )
    }

    return (
        <React.Fragment>

            {stepsCount > 1 ? renderGrouped() : renderDefault()}

        </React.Fragment>
    )

}